import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { customersMessages } from 'src/features/customers/utils/messages';
import { useGetPayment } from 'src/features/payments/api/useGetPayment';
import { PAYMENT_STATUS_BADGE_OPTIONS, PAYMENT_TYPE_BADGE_OPTIONS } from '../utils/constants';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import UpdatePaymentFormModal from 'src/features/payments/components/forms/UpdatePaymentFormModal';
import DeletePaymentFormModal from './forms/DeletePaymentFormModal';
import PaymentAllocationsTable from 'src/features/paymentAllocations/components/tables/PaymentAllocationsTable';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const PaymentDashboard = () => {
    const { formatMessage, formatNumber } = useIntl();
    const navigate = useNavigate();
    const { paymentId = '' } = useParams();

    const permissionManagePayments = useHasPermissions([PERMISSION.MANAGE_PAYMENTS]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(paymentsMessages.paymentDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: paymentData } = useGetPayment({
        paymentId: paymentId,
    });

    const hideEditDeleteButtons = paymentData?.allocated_amount == null || paymentData.allocated_amount > 0;

    const dashboardBlockData = {
        section1: [
            {
                title: customersMessages.customer,
                value: paymentData?.customer && (
                    <LinkWithIcon
                        to={`/customers/${paymentData?.customer?.id}`}
                        linkTitle={paymentData?.customer?.name}
                        hasPermissions={permissionAccessCustomers}
                    />
                ),
            },
            {
                title: commonMessages.date,
                value: getFormattedDateTimeForDetailPanel(paymentData?.date) ?? '-',
            },
            {
                title: paymentsMessages.type,
                value: paymentData ? (
                    <Badge theme={PAYMENT_TYPE_BADGE_OPTIONS[paymentData.type].theme || 'gray'}>
                        <FontAwesomeIcon icon={PAYMENT_TYPE_BADGE_OPTIONS[paymentData.type].icon as IconProp} />
                        {formatMessage(PAYMENT_TYPE_BADGE_OPTIONS[paymentData.type].label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            {
                title: generalMessages.status,
                value: paymentData?.status ? (
                    <Badge theme={PAYMENT_STATUS_BADGE_OPTIONS[paymentData.status].theme || 'gray'}>
                        <FontAwesomeIcon icon={PAYMENT_STATUS_BADGE_OPTIONS[paymentData.status].icon as IconProp} />
                        {formatMessage(PAYMENT_STATUS_BADGE_OPTIONS[paymentData.status].label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
        ],
        section2: [
            {
                title: commonMessages.currency,
                value: paymentData?.currency ? <CellCurrencyBadge currency={paymentData?.currency} /> : '-',
            },
            {
                title: paymentsMessages.amount,
                value: paymentData?.amount
                    ? formatNumber(paymentData?.amount / 100, { style: 'currency', currency: paymentData.currency })
                    : '-',
            },
            {
                title: paymentsMessages.allocatedAmount,
                value:
                    paymentData?.allocated_amount != null &&
                    formatNumber(paymentData?.allocated_amount / 100, {
                        style: 'currency',
                        currency: paymentData.currency,
                    }),
            },
            {
                title: paymentsMessages.remainingAmount,
                value:
                    paymentData?.remaining_amount != null &&
                    formatNumber(paymentData?.remaining_amount / 100, {
                        style: 'currency',
                        currency: paymentData.currency,
                    }),
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link" onClick={() => navigate(-1)}>
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(paymentsMessages.backToAllPayments)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {paymentData?.reference ?? getFormattedDateTimeForDetailPanel(paymentData?.date)}
                        </SectionBlockTitle>
                        {permissionManagePayments && paymentData && !hideEditDeleteButtons && (
                            <div className="flex flex-row gap-2">
                                <UpdatePaymentFormModal
                                    selectedPayment={paymentData}
                                    semantics={{
                                        entryName: formatMessage(paymentsMessages.payment).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                    </Button>
                                </UpdatePaymentFormModal>
                                <DeletePaymentFormModal
                                    selectedPayment={paymentData}
                                    semantics={{
                                        entryName: formatMessage(paymentsMessages.payment).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeletePaymentFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={1} />
                </SectionBlock>

                {/* A single table needs no tab row — a one-entry row is a control that cannot act. */}
                <div className="grid flex-3 grid-cols-1 pr-8">
                    <PaymentAllocationsTable
                        disableCreateButton={paymentData?.remaining_amount === 0}
                        currency={paymentData?.currency}
                    />
                </div>
            </div>
        </>
    );
};

export default PaymentDashboard;
